import React, { useEffect, useState, useRef, Fragment, createElement } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ShareIcon, CheckIcon } from 'lucide-react';
import { fetchSanity, urlForSanityImage, urlForSanityFile } from '../lib/sanity';
// Helper to convert a name like "Edith Yeung" to "edith-yeung"
const generateAuthorSlug = (name: string) => {
  if (!name) return '';
  return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
};
// Sanitize raw HTML pasted into Sanity before injecting it into the DOM.
// dangerouslySetInnerHTML does NOT run <script> tags, but inline event-handler
// attributes (onclick, onended, onload, etc.) DO execute in the global scope
// when fired. Snippets that reference undefined globals (e.g. onended="replay()")
// throw "ReferenceError: Can't find variable: replay". We strip those handlers
// and any <script> tags so authored markup renders without executing stray code.
const sanitizeHtml = (html: string): string => {
  if (!html || typeof html !== 'string') return '';
  return html
  // Remove <script>...</script> blocks entirely
  .replace(/<script[\s\S]*?<\/script>/gi, '')
  // Remove self-closing or unclosed <script ...> tags
  .replace(/<script[^>]*>/gi, '')
  // Strip inline event-handler attributes: onClick="...", onended='...', onload=foo()
  .replace(/\son[a-z]+\s*=\s*"[^"]*"/gi, '').replace(/\son[a-z]+\s*=\s*'[^']*'/gi, '').replace(/\son[a-z]+\s*=\s*[^\s>]+/gi, '')
  // Neutralize javascript: URLs
  .replace(/(href|src)\s*=\s*"javascript:[^"]*"/gi, '$1="#"').replace(/(href|src)\s*=\s*'javascript:[^']*'/gi, "$1='#'");
};
// Helper to inject responsive CSS overrides into the iframe HTML
// This fixes overlapping text and large labels in the embedded chart
const injectIframeStyles = (html: string): string => {
  if (!html || typeof html !== 'string') return html;
  const css = ['/* Overrides for embedded view to prevent text overlap */', '.b .nmtag { white-space: normal !important; width: 110px !important; }', '.b .nmtag .nm { font-size: 10.5px !important; line-height: 1.2 !important; white-space: normal !important; }', '.b .nmtag .yr { font-size: 8.5px !important; line-height: 1.2 !important; white-space: normal !important; margin-top: 4px !important; }', '.b .nmtag .vl { font-size: 9.5px !important; margin-top: 4px !important; }', '.tick label { font-size: 9px !important; }', '.bk .blab { font-size: 8.5px !important; letter-spacing: 0.1em !important; }', '.b.below.small .nmtag { top: calc(100% + 40px) !important; }', '.b.above.small .nmtag { bottom: calc(100% + 40px) !important; }', '/* Push the red arrow down so it clears the taller wrapped text */', '.gap { top: 390px !important; }', '.axwrap { height: 480px !important; }', '@media (max-width: 900px) { .axwrap { height: 440px !important; } }', '/* Add space below the main title */', 'h1 { margin-bottom: 24px !important; }', '/* Mobile-specific fixes for the chart */', '@media (max-width: 768px) {', '  /* Instead of squishing the complex chart, force a minimum width and allow horizontal scrolling */', '  body { overflow-x: auto !important; padding: 20px 16px !important; }', '  .sheet { min-width: 700px !important; padding-right: 20px !important; }', '}'].join('\n');
  // Build the style tag dynamically so the React formatter doesn't
  // try to wrap it in JSX brackets, which breaks the CSS in the iframe.
  const openTag = '<' + 'style' + '>';
  const closeTag = '</' + 'style' + '>';
  const styles = openTag + '\n' + css + '\n' + closeTag;
  // Inject right before </head> if it exists, otherwise append to the end
  if (html.toLowerCase().includes('</head>')) {
    return html.replace(/<\/head>/i, `${styles}</head>`);
  }
  return html + styles;
};
const AutoPlayVideo = ({
  src,
  caption
}: {
  src: string;
  caption?: string;
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  useEffect(() => {
    if (videoRef.current) {
      // Force autoplay as soon as the component mounts
      videoRef.current.play().catch(() => {
        // Ignore errors (e.g., iOS Low Power Mode blocks autoplay)
      });
    }
  }, [src]);
  return <figure className="my-16 md:-mx-16 lg:-mx-32" data-id="element-157" data-mp="lwomikqpk7ul">
      <video ref={videoRef} src={src} autoPlay loop muted playsInline className="w-full rounded-2xl bg-gray-100 shadow-lg cursor-pointer" onClick={e => {
      const video = e.currentTarget;
      if (video.paused) {
        video.play();
      } else {
        video.pause();
      }
    }} data-id="element-158" data-mp="lx82vx0pmo6i" />
      {caption && <figcaption className="text-center text-sm text-gray-500 mt-4" data-id="element-159" data-mp="14tpp5ywshvvd">
          {caption}
        </figcaption>}
    </figure>;
};
export const BlogPostPage = ({
  basePath = '/blog'
}: {
  basePath?: string;
}) => {
  const {
    slug
  } = useParams();
  const [post, setPost] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    window.scrollTo(0, 0);
    const getPost = async () => {
      try {
        const query = `*[_type == "post" && (slug.current == "${slug}" || _id == "${slug}")][0] {
          title,
          author,
          "publishedAt": coalesce(publishedAt, _createdAt),
          "imageUrl": mainImage.asset->url,
          body
        }`;
        const data = await fetchSanity(query);
        if (data) {
          setPost(data);
          document.title = `${data.title} | Race Capital`;
          // Inject SEO Meta Tags dynamically
          const setMetaTag = (name: string, content: string, isProperty = false) => {
            const attr = isProperty ? 'property' : 'name';
            let element = document.querySelector(`meta[${attr}="${name}"]`);
            if (!element) {
              element = document.createElement('meta');
              element.setAttribute(attr, name);
              document.head.appendChild(element);
            }
            element.setAttribute('content', content);
          };
          // Extract a plain text excerpt from the body for the description
          let excerpt = 'Read the latest from Race Capital.';
          if (typeof data.body === 'string') {
            // Strip HTML tags
            excerpt = data.body.replace(/<[^>]*>?/gm, '').substring(0, 160) + '...';
          } else if (Array.isArray(data.body)) {
            const firstTextBlock = data.body.find((b: any) => b._type === 'block' && b.children && b.children[0]?.text);
            if (firstTextBlock) {
              excerpt = firstTextBlock.children[0].text.substring(0, 160) + '...';
            }
          }
          setMetaTag('description', excerpt);
          setMetaTag('og:title', data.title, true);
          setMetaTag('og:description', excerpt, true);
          setMetaTag('og:type', 'article', true);
          if (data.imageUrl) {
            setMetaTag('og:image', data.imageUrl, true);
            setMetaTag('twitter:image', data.imageUrl);
          }
          setMetaTag('twitter:card', 'summary_large_image');
          setMetaTag('twitter:title', data.title);
          setMetaTag('twitter:description', excerpt);
        } else {
          setPost(null);
        }
      } catch (err) {
        console.error('Failed to fetch post:', err);
        setPost(null);
      } finally {
        setLoading(false);
      }
    };
    if (slug) {
      getPost();
    }
  }, [slug]);
  const handleShare = async () => {
    const url = window.location.href;
    try {
      // Try native share first (will likely fail inside an iframe)
      if (navigator.share) {
        await navigator.share({
          title: post?.title || 'Race Capital Blog',
          url: url
        });
        return; // If successful, we're done
      }
    } catch (err) {
      console.log('Native share not supported or blocked, falling back to clipboard.');
    }
    // Fallback: Copy to clipboard
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Ultimate fallback if clipboard API is blocked
      const textArea = document.createElement('textarea');
      textArea.value = url;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };
  if (loading) {
    return <div className="w-full min-h-screen bg-white pt-40 pb-32" data-id="element-160" data-mp="20ra6mansecjj">
        <div className="max-w-7xl mx-auto px-6 md:px-12 animate-pulse" data-id="element-161" data-mp="1mqhdnud4vpij">
          <div className="w-24 h-4 bg-gray-200 rounded mb-12" data-id="element-162" data-mp="1qskyvaplu8h0"></div>
          <div className="max-w-3xl mx-auto mb-16" data-id="element-163" data-mp="1qrt2hxo6o6po">
            <div className="w-full h-14 md:h-20 bg-gray-200 rounded mb-4" data-id="element-164" data-mp="183sddqb258b3"></div>
            <div className="w-3/4 h-14 md:h-20 bg-gray-200 rounded" data-id="element-165" data-mp="1357dxt3mqsnp"></div>
          </div>
          <div className="max-w-3xl mx-auto w-full aspect-[2/1] rounded-2xl bg-gray-100 mb-20" data-id="element-166" data-mp="137k3arbpw5as"></div>
          <div className="max-w-3xl mx-auto space-y-6" data-id="element-167" data-mp="1h2m8fc8qpqmj">
            <div className="w-full h-4 bg-gray-200 rounded" data-id="element-168" data-mp="1c547o94vmhpm"></div>
            <div className="w-full h-4 bg-gray-200 rounded" data-id="element-169" data-mp="1r2mqkuuvi0p5"></div>
            <div className="w-5/6 h-4 bg-gray-200 rounded" data-id="element-170" data-mp="1r0tjxfk9fgh5"></div>
            <div className="w-full h-4 bg-gray-200 rounded mt-8" data-id="element-171" data-mp="1m3a6uizat9qs"></div>
            <div className="w-4/5 h-4 bg-gray-200 rounded" data-id="element-172" data-mp="1zvzvywa4ius4"></div>
          </div>
        </div>
      </div>;
  }
  if (!post) {
    return <div className="w-full min-h-screen bg-white pt-40 pb-32 flex flex-col items-center justify-center" data-id="element-173" data-mp="1vuobt6a0r1rb">
        <h1 className="text-3xl font-bold text-[#070707] mb-4" data-id="element-174" data-mp="29rjmt49tz3a0">
          Post not found
        </h1>
        <Link to="/blog" className="text-red-600 hover:underline" data-id="element-175" data-mp="24sgi5kxriz2k">
          Return to Blog
        </Link>
      </div>;
  }
  const isArchitectBlog = post?.title?.toLowerCase().includes('architect') || slug?.toLowerCase().includes('architect');
  return <div className="w-full min-h-screen bg-white pt-40 pb-32" data-id="element-176" data-mp="wgvoyg9xuz32">
      <div className="max-w-7xl mx-auto px-6 md:px-12" data-id="element-177" data-mp="rh9cwa5ujovu">
        <div className="flex flex-col md:flex-row md:items-start justify-between mb-12" data-id="element-178" data-mp="riu71yo915t6">
          {/* Back Button */}
          <Link to={basePath} className="inline-flex items-center text-sm font-semibold text-gray-500 hover:text-red-600 transition-colors uppercase tracking-wider mb-8 md:mb-0 group" data-id="element-179" data-mp="1o4xg7m191cvc">
            <ArrowLeftIcon className="w-4 h-4 mr-2 transition-transform group-hover:-translate-x-1" data-id="element-180" data-mp="1a7qzdx035o8r" />
            Back to Blog
          </Link>
        </div>

        {/* Title */}
        <div className="max-w-3xl mx-auto mb-16" data-id="element-181" data-mp="3p2aoudfwnoi">
          <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold text-[#070707] leading-[1.1] tracking-tight" data-id="element-182" data-mp="3uwolr82hnsfe">
            {post.title || 'Untitled Post'}
          </h1>
        </div>

        {/* Featured Image */}
        {post.imageUrl && <div className="max-w-3xl mx-auto w-full aspect-[2/1] rounded-2xl overflow-hidden mb-20 bg-gray-100" data-id="element-183" data-mp="3pqjbuec6o1oj">
            <img src={post.imageUrl} alt={post.title} className="w-full h-full object-cover" data-id="element-184" data-mp="3d1c0w3fnr4j" />
          </div>}

        {/* Content */}
        <div className="max-w-3xl mx-auto" data-id="element-185" data-mp="cdpqfrwqnthy">
          <div className="flex items-end justify-between border-b border-gray-200 pb-8 mb-12" data-id="element-186" data-mp="ccoc9ekfeqw0">
            <div data-id="element-187" data-mp="hedsd8mi9uqy">
              {post.author && <div className="mb-2 text-lg text-[#070707] font-semibold" data-id="element-188" data-mp="mc5fzqg4aupc">
                  {post.author.split(',').map((authorName: string, index: number, array: string[]) => {
                const cleanName = authorName.trim();
                return <Fragment key={index} data-id="element-189" data-mp="2g3m3kvtlug2d" data-mpi={index}>
                            <Link to={`/team/${generateAuthorSlug(cleanName)}`} className="hover:text-red-600 transition-colors" data-id="element-190" data-mp="2b3rd2tdqrshu">
                              {cleanName}
                            </Link>
                            {index < array.length - 1 && ', '}
                          </Fragment>;
              })}
                </div>}
              <div className="text-gray-500 font-medium" data-id="element-191" data-mp="38ri6ii9rp47l">
                {post.publishedAt ? `Published on ${new Date(post.publishedAt).toLocaleDateString()}` : 'Recently published'}
              </div>
            </div>
            <div className="flex space-x-4 mb-1" data-id="element-192" data-mp="3hspsq1z0eelb">
              <button onClick={handleShare} className={`flex items-center transition-colors font-medium text-sm uppercase tracking-wider ${copied ? 'text-green-600' : 'text-gray-400 hover:text-red-600'}`} data-id="element-193" data-mp="3dquvb8k78lct">
                {copied ? <>
                    <CheckIcon className="w-4 h-4 mr-2" data-id="element-194" data-mp="3neqbh1chgdb4" />
                    Copied!
                  </> : <>
                    <ShareIcon className="w-4 h-4 mr-2" data-id="element-195" data-mp="3sbgsps00xey4" />
                    Share
                  </>}
              </button>
            </div>
          </div>

          <div className="prose prose-lg md:prose-xl max-w-none prose-headings:font-bold prose-headings:text-[#070707] prose-headings:tracking-tight prose-p:text-gray-700 prose-p:leading-relaxed prose-a:text-red-600 prose-a:no-underline hover:prose-a:underline prose-blockquote:border-l-red-600 prose-blockquote:text-gray-900 prose-blockquote:font-medium prose-blockquote:italic prose-blockquote:bg-gray-50 prose-blockquote:py-2 prose-blockquote:px-6 prose-blockquote:rounded-r-lg prose-li:text-gray-700" data-id="element-196" data-mp="3ifvw69dq56vr">
            {typeof post.body === 'string' ? post.body.toLowerCase().includes('<script') || post.body.toLowerCase().includes('<!doctype') ? <iframe srcDoc={injectIframeStyles(post.body)} className={`w-full border-0 rounded-2xl transition-all duration-300 ${isArchitectBlog ? 'hidden md:block my-4' : 'my-12'}`} style={{
            minHeight: isArchitectBlog ? '300px' : '600px'
          }} onLoad={e => {
            const iframe = e.currentTarget;
            if (iframe.contentWindow) {
              const buffer = isArchitectBlog ? 10 : 40;
              // Auto-resize to perfectly fit the content
              iframe.style.height = iframe.contentWindow.document.documentElement.scrollHeight + buffer + 'px';
            }
          }} sandbox="allow-scripts allow-same-origin allow-popups" title="Embedded Post" data-id="element-197" data-mp="3t8xaxjzq0r77" /> : <div dangerouslySetInnerHTML={{
            __html: sanitizeHtml(post.body)
          }} data-id="element-198" data-mp="1kv1za1qfjh73" /> : Array.isArray(post.body) ?
          // Enhanced Portable Text Renderer for preview purposes
          <div data-id="element-199" data-mp="x3d48dm84emg">
                {post.body.map((block: any, index: number) => {
              // Handle image blocks
              if (block._type === 'image') {
                const imageUrl = urlForSanityImage(block);
                if (!imageUrl) return null;
                const isArchitectBlog = post?.title?.toLowerCase().includes('architect') || slug?.toLowerCase().includes('architect');
                const isGif = imageUrl.toLowerCase().includes('.gif');
                const hideOnDesktop = isArchitectBlog && isGif;
                return <figure key={index} className={`my-12 ${hideOnDesktop ? 'md:hidden' : ''}`} data-id="element-200" data-mp="di9dt0nzubf3">
                        <img src={imageUrl} alt={block.alt || 'Blog post image'} className="w-full rounded-2xl bg-gray-100" data-id="element-201" data-mp="mi4ufl0biymi" />
                        {block.caption && <figcaption className="text-center text-sm text-gray-500 mt-3" data-id="element-202" data-mp="8hll8ak49mir">
                            {block.caption}
                          </figcaption>}
                      </figure>;
              }
              // Handle video/file blocks
              if (block._type === 'file' || block._type === 'video') {
                const fileUrl = urlForSanityFile(block);
                if (!fileUrl) return null;
                return <AutoPlayVideo key={index} src={fileUrl} caption={block.caption} data-id="element-203" data-mp="1rgjkb2x9gycb" />;
              }
              // Handle custom HTML/Embed blocks
              if (block._type === 'html' || block._type === 'embed' || block._type === 'code') {
                const htmlContent = block.code || block.html || block.content;
                if (!htmlContent) return null;
                // If the snippet contains scripts or is a full HTML document,
                // render it safely inside an iframe so the JS can execute
                // without crashing the parent React app.
                if (htmlContent.toLowerCase().includes('<script') || htmlContent.toLowerCase().includes('<!doctype')) {
                  return <iframe key={index} srcDoc={injectIframeStyles(htmlContent)} className={`w-full border-0 rounded-2xl transition-all duration-300 ${isArchitectBlog ? 'hidden md:block my-4' : 'my-12'}`} style={{
                    minHeight: isArchitectBlog ? '300px' : '600px'
                  }} onLoad={e => {
                    const iframe = e.currentTarget;
                    if (iframe.contentWindow) {
                      const buffer = isArchitectBlog ? 10 : 40;
                      // Auto-resize to perfectly fit the content
                      iframe.style.height = iframe.contentWindow.document.documentElement.scrollHeight + buffer + 'px';
                    }
                  }} sandbox="allow-scripts allow-same-origin allow-popups" title="Embedded Content" data-id="element-204" data-mp="17nw8xy2ddrkw" />;
                }
                return <div key={index} className="my-12 w-full overflow-x-auto" dangerouslySetInnerHTML={{
                  __html: sanitizeHtml(htmlContent)
                }} data-id="element-205" data-mp="35ia574be5ff2" />;
              }
              // Handle standalone embed/link blocks that aren't standard text blocks
              if (block._type !== 'block' || !block.children) {
                const linkUrl = block.url || block.href;
                if (linkUrl) {
                  return <div key={index} className="my-6 p-4 bg-gray-50 border border-gray-200 rounded-lg" data-id="element-206" data-mp="10lqkbz9n6kxl">
                          <a href={linkUrl} target="_blank" rel="noopener noreferrer" className="text-red-600 underline font-medium break-all" data-id="element-207" data-mp="1ak6d06obi297">
                            {block.title || linkUrl}
                          </a>
                        </div>;
                }
                return null;
              }
              const isEmpty = block.children.every((child: any) => !child.text || child.text.trim() === '');
              if (isEmpty) {
                return <p key={index} className="min-h-[1.5em] m-0" data-id="element-208" data-mp="3mtejsoxbompi">
                        &nbsp;
                      </p>;
              }
              const childrenContent = block.children.map((child: any, cIndex: number) => {
                let content: React.ReactNode = child.text;
                // Handle soft line breaks (\n) inside text blocks
                if (typeof content === 'string' && content.includes('\n')) {
                  content = content.split('\n').map((line, i, arr) => <Fragment key={i} data-id="element-209" data-mp="klsgle5m578" data-mpi={i}>
                            {line}
                            {i < arr.length - 1 && <br data-id="element-210" data-mp="3rs7qnyi3tfgp" />}
                          </Fragment>);
                }
                if (child.marks && child.marks.length > 0) {
                  child.marks.forEach((mark: string) => {
                    if (mark === 'strong') {
                      content = <strong key={mark} data-id="element-211" data-mp="2ilpkgyhsocmt">{content}</strong>;
                    } else if (mark === 'em') {
                      content = <em key={mark} data-id="element-212" data-mp="28u1s8vdeyrka">{content}</em>;
                    } else {
                      // Check for complex marks like links in markDefs
                      const def = block.markDefs?.find((d: any) => d._key === mark);
                      if (def && (def._type === 'link' || def.href || def.url)) {
                        const href = def.href || def.url;
                        content = <a key={mark} href={href} target="_blank" rel="noopener noreferrer" className="text-red-600 underline decoration-red-600/30 hover:decoration-red-600 underline-offset-2 font-medium transition-all" data-id="element-213" data-mp="3k8f6d8id7sn4">
                                  {content}
                                </a>;
                      }
                    }
                  });
                }
                return <Fragment key={cIndex} data-id="element-214" data-mp="lv926gm75u63" data-mpi={cIndex}>{content}</Fragment>;
              });
              // Handle list items
              if (block.listItem === 'bullet') {
                return <li key={index} className="ml-6 list-disc my-2" data-id="element-215" data-mp="qw6p16i5jcdx">
                        {childrenContent}
                      </li>;
              }
              if (block.listItem === 'number') {
                return <li key={index} className="ml-6 list-decimal my-2" data-id="element-216" data-mp="2spcpoft72k37">
                        {childrenContent}
                      </li>;
              }
              // Handle basic styles
              if (block.style === 'h1') return <h1 key={index} className="text-4xl md:text-5xl font-bold mt-16 mb-8 text-[#070707] tracking-tight" data-id="element-217" data-mp="3366k0eifcsah">
                        {childrenContent}
                      </h1>;
              if (block.style === 'h2') return <h2 key={index} className="text-3xl md:text-4xl font-bold mt-14 mb-6 text-[#070707] tracking-tight" data-id="element-218" data-mp="2kenikc41tmak">
                        {childrenContent}
                      </h2>;
              if (block.style === 'h3') return <h3 key={index} className="text-2xl md:text-3xl font-bold mt-10 mb-4 text-[#070707] tracking-tight" data-id="element-219" data-mp="1wmyksg8gk5m8">
                        {childrenContent}
                      </h3>;
              if (block.style === 'h4') return <h4 key={index} className="text-xl md:text-2xl font-bold mt-8 mb-4 text-[#070707]" data-id="element-220" data-mp="1b595uzdtsmgm">
                        {childrenContent}
                      </h4>;
              if (block.style === 'blockquote') return <blockquote key={index} className="my-12 border-l-4 border-red-600 bg-gray-50 py-8 px-8 md:px-10 rounded-r-2xl text-xl md:text-2xl font-medium italic text-gray-900 leading-relaxed relative" data-id="element-221" data-mp="ri1rh1konh2j">
                        <div className="absolute top-4 left-4 text-red-200 text-6xl font-serif leading-none select-none" aria-hidden="true" data-id="element-222" data-mp="ijgahauv0a3v">
                          "
                        </div>
                        <div className="relative z-10" data-id="element-223" data-mp="3v2irrhshvx7m">{childrenContent}</div>
                      </blockquote>;
              return <p key={index} data-id="element-224" data-mp="rk8y2r8xg6u4" data-mpi={index}>{childrenContent}</p>;
            })}
              </div> : <p data-id="element-225" data-mp="wj2eosjigdyi">No content available.</p>}
          </div>
        </div>
      </div>
    </div>;
};